import { ASSESSMENT_RISK_LEVELS, ASSESSMENT_STATUSES } from '@nestjs-boilerplate/types';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import type { AdminAssessmentFilters } from '@/features/admin/api/assessments';
import { AssessmentsPage } from '@/features/admin/components/assessments-page';
import { type PaginationSearch, validatePaginationSearch } from '@/lib/pagination-search';
import { manyOf } from '@/lib/query-params';

/**
 * Search params the list accepts: the filter set, paging and ordering.
 *
 * All of it lives in the URL, as it does on the user list, so a filtered view
 * survives a drill-down into an assessment and back — the normal flow on this
 * screen, and previously the one that lost your filters. The history spam that
 * argued for component state is handled instead by the two things that actually
 * address it: the free-text boxes debounce, and every write replaces rather than
 * pushes.
 */
export interface AssessmentsSearch extends AdminAssessmentFilters, PaginationSearch {}

/**
 * Narrows the search params, dropping anything the API would reject.
 *
 * Anything can be typed into an address bar, and an unrecognised value must fall
 * back rather than become a failed request.
 */
function validateSearch(search: Record<string, unknown>): AssessmentsSearch {
  const result: AssessmentsSearch = validatePaginationSearch(search);

  for (const key of ['search', 'owner', 'company'] as const) {
    const value = search[key];
    if (typeof value === 'string' && value) {
      result[key] = value;
    }
  }

  // Both accept several values at once, comma-separated. An unrecognised value
  // is dropped rather than rejected — nothing here can report an error to the
  // reader, so a mistyped link degrades to a wider list.
  const riskLevel = manyOf(ASSESSMENT_RISK_LEVELS, search.riskLevel);
  if (riskLevel) result.riskLevel = riskLevel;
  // Selecting no statuses leaves the parameter out, which is what makes the API
  // fall back to "everything except deleted".
  const status = manyOf(ASSESSMENT_STATUSES, search.status);
  if (status) result.status = status;
  // Date bounds are kept only in the `YYYY-MM-DD` form the picker produces and
  // the API expects. Anything else is dropped rather than forwarded, so a
  // hand-edited URL degrades to an unfiltered list instead of a 400.
  for (const key of ['from', 'to'] as const) {
    const value = search[key];
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      result[key] = value;
    }
  }

  if (
    search.sortBy === 'createdAt' ||
    search.sortBy === 'riskLevel' ||
    search.sortBy === 'status'
  ) {
    result.sortBy = search.sortBy;
  }
  if (search.sortOrder === 'asc' || search.sortOrder === 'desc') {
    result.sortOrder = search.sortOrder;
  }

  return result;
}

export const Route = createFileRoute('/admin/assessments/')({
  validateSearch,
  component: AssessmentsRoute,
});

function AssessmentsRoute() {
  const filters = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <AssessmentsPage
      filters={filters}
      onFiltersChange={(next) => navigate({ search: next, replace: true })}
    />
  );
}
